import React from 'react';
import { Navigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { BrandedLoading } from '@/components/common/BrandedLoading';

/** Extract `exp` claim (ms) from JWT payload. Returns 0 on failure/unparseable. */
function getTokenExpMs(token: string): number {
  try {
    const base64Url = token.split('.')[1] || '';
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const jsonPayload = atob(padded);
    const expSeconds = JSON.parse(jsonPayload).exp;
    return typeof expSeconds === 'number' ? expSeconds * 1000 : 0;
  } catch {
    return 0;
  }
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRoles }) => {
  const { isAuthenticated, isLoading, user, accessToken } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4">
        <BrandedLoading message="Completing authentication…" iconSize="md" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // In "token-only" bootstrap, `user` may be null initially while the access token is still valid.
    // Also check localStorage directly — React state may not have flushed yet after bootstrap.
    const storedToken = accessToken || (typeof window !== 'undefined' ? localStorage.getItem('five_ai_access_token') : null);
    if (storedToken) {
      const expMs = getTokenExpMs(storedToken);
      const now = Date.now();
      if (expMs === 0 || expMs > now) {
        // Token exists and not expired — wait for AuthContext to finish hydrating
        return (
          <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4">
            <BrandedLoading message="Completing authentication…" iconSize="md" />
          </div>
        );
      }
    }
    // No valid token — redirect to login with return URL
    const nextUrl = location.pathname + location.search;
    return <Navigate to={`/login?next=${encodeURIComponent(nextUrl)}`} replace />;
  }

  if (requiredRoles && requiredRoles.length > 0) {
    const userRoles: string[] = user?.roles ?? [];
    const hasRequiredRole = requiredRoles.some((role) => userRoles.includes(role));

    if (!hasRequiredRole) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="w-full max-w-md rounded-lg border border-destructive/20 bg-destructive/5 p-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <svg
                className="h-6 w-6 text-destructive"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"
                />
              </svg>
            </div>
            <h2 className="mb-2 text-lg font-semibold text-foreground">Access Denied</h2>
            <p className="mb-6 text-sm text-muted-foreground">
              You do not have the required permissions to access this page.
            </p>
            <Link
              to="/auth"
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Back to Sign In
            </Link>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
